import CancelButton from "@common/controls/Form/Buttons/Cancel";
import SubmitButton from "@common/controls/Form/Buttons/Submit";
import { FormButtons, FormField } from "@common/controls/Form/component";
import { TextInput } from "@common/controls/Inputs/Text";
import ConfirmSuccessErrorModal from "@common/controls/Modal/Modals/ConfirmSuccessError/component";
import { type FormModalControlledRenderParams } from "@common/controls/Modal/Modals/Form/component";
import withShow, { type WithShowProps } from "@common/controls/Modal/Modals/hocs/withShow";
import withSubmit, { type WithSubmitProps } from "@common/controls/Modal/Modals/hocs/withSubmit";
import { useI18n } from "@common/hooks/i18n";
import { formatMoneyDisplay } from "@common/lib/format";
import { useVisibleModal } from "@common/lib/hooks";
import { i18nFormat } from "@common/lib/i18n";
import { getAccountButtonCaption } from "@common/modules/account/lib";
import { type Account } from "@common/modules/account/types";
import * as React from "react";
import MoneyWithCurrencyFormField from "../Form/Field/MoneyWithCurrency/component";
import FormModalOtpControlled from "../Modal/FormModalOtpControlled";
import { type OtpStep } from "../Otp/Modal";
import { type OtpApi } from "../Otp/types";
import AddCreateRecipientFormField from "./AddCreateRecipientFormField";
import { type TransferApi } from "./types";

type TransferModalProps = WithSubmitProps<Account.Api.TransferRequest, Account.Api.AccountTransaction | null> &
  WithShowProps & {
    api: TransferApi;
    defaultAccount: Account.Account;
    isVisible: boolean;
    onClose: () => void;
    recipientPreviewStyle?: React.CSSProperties;
    userAccounts: Account.Account[];
  };
export const TransferModal = (props: TransferModalProps) => {
  const i18n = useI18n();

  const [currentAccount, setCurrentAccount] = React.useState<Account.Account>(props.defaultAccount);
  React.useEffect(() => setCurrentAccount(props.defaultAccount), [props.defaultAccount]);

  const [transaction, setTransaction, unsetTransaction] = useVisibleModal<Account.Api.AccountTransaction>();

  const defaultValues: Partial<Account.View.TransferData> = React.useMemo(
    () => ({
      currencyCode: props.defaultAccount?.currencyCode,
      amount: 0
    }),
    [props.defaultAccount?.currencyCode]
  );

  const submit = React.useCallback(
    async (data: Account.View.TransferData) => {
      const accountId = props.userAccounts.find((x) => x.currencyCode === data.currencyCode)!.accountId;
      const transaction = await props.submit({ ...data, fromAccountId: accountId });
      if (transaction) {
        setTransaction(transaction);
      }
      return !!transaction;
    },
    [props.submit, props.userAccounts]
  );

  const getOtpApi = React.useCallback(
    (data: Account.View.TransferData): OtpApi => ({
      getSteps: () => {
        const accountId = props.userAccounts.find((x) => x.currencyCode === data.currencyCode)!.accountId;
        return props.api.otp.getSteps(accountId);
      },
      sendCode: (step: OtpStep) => {
        const accountId = props.userAccounts.find((x) => x.currencyCode === data.currencyCode)!.accountId;
        return props.api.otp.sendCode(accountId, step.messageType);
      }
    }),
    [props.userAccounts, props.api]
  );

  const onConfirmClose = React.useCallback(() => {
    unsetTransaction();
    props.onClose();
  }, [props.onClose]);

  return (
    <>
      <FormModalOtpControlled
        className="transfer_modal"
        defaultValues={defaultValues}
        isVisible={props.isVisible}
        onClose={props.onClose}
        getOtpApi={getOtpApi}
        submit={submit}
        title={i18n.transfer}
      >
        {({ defaultValues, values, onChange }: FormModalControlledRenderParams<Account.View.TransferData>) => (
          <>
            <MoneyWithCurrencyFormField
              amount={values.amount}
              amountName="amount"
              defaultCurrency={defaultValues.currencyCode}
              currency={values.currencyCode}
              currencyName="currencyCode"
              hint={`${i18n.balance}: ${formatMoneyDisplay(currentAccount.balance, currentAccount.currencyCode)}`}
              label={i18n.amount}
              isRequired
              componentProps={{
                max: currentAccount.balance,
                min: 0,
                options: props.userAccounts.map((x) => ({
                  value: x.currencyCode,
                  label: x.currencyCode.toUpperCase(),
                  isCommission: true
                }))
              }}
              onChange={(name, value) => {
                onChange(name as any, value);

                if (name === "currencyCode") {
                  setCurrentAccount(props.userAccounts.find((x) => x.currencyCode === value)!);
                }
              }}
              qa="Amount"
            />

            <AddCreateRecipientFormField
              api={props.api}
              isRequired
              label={i18n.recipient}
              name="beneficiaryId"
              recipientStyle={props.recipientPreviewStyle}
              value={values.beneficiaryId}
            />

            {values.beneficiaryId && (
              <>
                <FormField
                  Component={TextInput}
                  componentProps={{
                    maxLength: 32,
                    isMaxLengthVisible: true
                  }}
                  isRequired
                  label={i18n.description}
                  name="description"
                  value={values.description}
                  qa="Description"
                  tooltip={i18n.infoTransferReference}
                />
              </>
            )}

            <FormButtons>
              <CancelButton />
              <SubmitButton
                isDisabled={
                  !values.amount ||
                  currentAccount.minTransferAmount > values.amount ||
                  values.amount > currentAccount.balance
                }
              >
                {getAccountButtonCaption(i18n, {
                  action: "transfer",
                  amount: values.amount,
                  balance: currentAccount.balance,
                  minTransferAmount: currentAccount.minTransferAmount,
                  currencyCode: currentAccount.currencyCode
                })}
              </SubmitButton>
            </FormButtons>
          </>
        )}
      </FormModalOtpControlled>

      <ConfirmSuccessErrorModal
        isVisible={!!transaction}
        title={i18n.transfer}
        isSuccess
        text={i18nFormat(i18n.infoSuccessTransferText, [
          "AMOUNT",
          formatMoneyDisplay(transaction?.amount, transaction?.accountCurrencyCode)
        ])}
        onClose={onConfirmClose}
      />
    </>
  );
};

const TransferModalWithShowSubmit = withShow(withSubmit(TransferModal));
export default TransferModalWithShowSubmit;
